import { apiClient } from '../client';
import type { Paginated, PaginationQuery } from './types';

export interface Account {
  id: number;
  parentId: number | null;
  name: string;
  shortName: string | null;
  taxId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  zip: string | null;
  city: string | null;
  state: string | null;
  region: string | null;
  countryId: number | null;
  phone: string | null;
  phoneAlt: string | null;
  fax: string | null;
  website: string | null;
  email: string | null;
  businessLine: string | null;
  businessLine2: string | null;
  businessLine3: string | null;
  category: string | null;
  categoryAlt: string | null;
  assignedToId: number | null;
  primaryPersonId: number | null;
  archived: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  country?: { id: number; code: string; name: string } | null;
  assignedTo?: { id: number; firstName: string; lastName: string } | null;
  parent?: { id: number; name: string } | null;
  primaryPerson?: {
    id: number;
    firstName: string | null;
    lastName: string;
    jobTitle: string | null;
    email: string | null;
    phone: string | null;
  } | null;
}

/** Contacto principal opcional al crear una cuenta (crea el Contacto). */
export interface PrimaryContactInput {
  firstName?: string;
  lastName: string;
  jobTitle?: string;
  email?: string;
  phone?: string;
  mobile?: string;
}

export type AccountListItem = Pick<
  Account,
  | 'id'
  | 'name'
  | 'shortName'
  | 'email'
  | 'phone'
  | 'city'
  | 'businessLine'
  | 'category'
  | 'archived'
  | 'createdAt'
  | 'country'
>;

export interface AccountListQuery extends PaginationQuery {
  countryId?: number;
  assignedToId?: number;
  archived?: boolean;
  favoritesOnly?: boolean;
  keywordIds?: string;
}

export type AccountInput = Partial<
  Omit<Account, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'primaryPerson'>
> & {
  name: string;
  primaryContact?: PrimaryContactInput;
};

export const accountsApi = {
  async list(q: AccountListQuery): Promise<Paginated<AccountListItem>> {
    const { data } = await apiClient.get<Paginated<AccountListItem>>('/accounts', { params: q });
    return data;
  },
  async findOne(id: number): Promise<Account> {
    const { data } = await apiClient.get<Account>(`/accounts/${id}`);
    return data;
  },
  async create(input: AccountInput): Promise<Account> {
    const { data } = await apiClient.post<Account>('/accounts', input);
    return data;
  },
  async update(id: number, input: Partial<AccountInput>): Promise<Account> {
    const { data } = await apiClient.patch<Account>(`/accounts/${id}`, input);
    return data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/accounts/${id}`);
  },
};
